// src/components/data-sources/ConnectorIcon.tsx

import MetaIcon from "./icons/meta.svg?react";
import GoogleAnalyticsIcon from "./icons/google-analytics.svg?react";
import GoogleAdsIcon from "./icons/google-ads.svg?react";
import BigQueryIcon from "./icons/bigquery.svg?react";
import ShopifyIcon from "./icons/shopify.svg?react";
import MicrosoftAdsIcon from "./icons/microsoft-ads.svg?react";
import ExactIcon from "./icons/exact.svg?react";
import BrevoIcon from "./icons/brevo.svg?react";
import ActiveCampaignIcon from "./icons/activecampaign.svg?react";
import KlaviyoIcon from "./icons/klaviyo.svg?react";
import GoogleSearchConsoleIcon from "./icons/google-search-console.svg?react";
import WooCommerceIcon from "./icons/woocommerce.svg?react";
import {
  IconPlugConnected,
  IconSnowflake,
  IconSql,
  IconWorldCode,
} from "@tabler/icons-react";

import { CONNECTOR_ICON_FILES } from "./connectorIconFiles";
import { resolveRegistryKey } from "./registry-mapping";

type Props = {
  type?: string;
  size?: number;
};

function normalizeType(type?: string): string {
  if (!type) return "";
  const parts = String(type).split(".");
  return parts[parts.length - 1].toUpperCase();
}

/**
 * The connector types this component has a real brand logo for.
 *
 * Exported so callers that need a *different* fallback than the generic
 * plug icon (e.g. the dashboards overview, which falls back to the
 * dashboard's own stored icon) can ask first instead of duplicating this
 * list — one place to update when a connector's SVG lands.
 *
 * Keep in step with the switch below.
 */
export const BRAND_CONNECTOR_TYPES: ReadonlySet<string> = new Set([
  "FACEBOOK",
  "GOOGLE_ANALYTICS",
  "GA4_BQ_EXPORT",
  "GOOGLE_ADS",
  "CUSTOM_BIGQUERY",
  "SHOPIFY",
  "MICROSOFT_ADS",
  "EXACT",
  "BREVO",
  "ACTIVECAMPAIGN",
  "KLAVIYO",
  "GOOGLE_SEARCH_CONSOLE",
  "WOOCOMMERCE",
  // Marks shipped as `icons/connectors/<module>.svg` (connectorIconFiles.ts).
  ...Object.keys(CONNECTOR_ICON_FILES),
]);

/** Whether `type` resolves to a real brand logo (not the generic fallback). */
export function hasBrandIcon(type?: string): boolean {
  return BRAND_CONNECTOR_TYPES.has(normalizeType(type));
}

// ---------------------------------------------------------------------------
// Catalog-side resolvers: a catalog entry carries `connector_key` (the data
// source's ds_key) and `warehouse_kind` — neither is a connector type, so the
// block editor's Source select cannot feed them to <ConnectorIcon /> directly.
//
// The ds_key map below is deliberately keyed on the REGISTRY FAMILY, never on
// ds_keys, so this file does not become a second ds_key list that drifts from
// the first. `registry-mapping.ts` already mirrors the backend's
// SOURCE_TYPE_REGISTRY and owns the ds_key → family lookup, and its drift test
// (registry-mapping.test.ts) fails when the backend gains a family this mirror
// lacks. A new connector therefore arrives here through that same test plus
// the coverage guard in ConnectorIcon.registryMap.test.ts, instead of silently
// rendering the generic plug.
// ---------------------------------------------------------------------------

/** Registry family → the connector type whose mark represents it. */
export const REGISTRY_KEY_TO_CONNECTOR_TYPE: Readonly<Record<string, string>> = {
  meta: "FACEBOOK",
  "google-ads": "GOOGLE_ADS",
  ga4: "GOOGLE_ANALYTICS",
  "microsoft-ads": "MICROSOFT_ADS",
  "exact-online": "EXACT",
  brevo: "BREVO",
  activecampaign: "ACTIVECAMPAIGN",
  klaviyo: "KLAVIYO",
  shopify: "SHOPIFY",
  woocommerce: "WOOCOMMERCE",
  gsc: "GOOGLE_SEARCH_CONSOLE",
  "verity-pixel": "VERITY_PIXEL",
  "other-bq": "CUSTOM_BIGQUERY",
  "other-sql": "CUSTOM_SQL",
  "other-snowflake": "SNOWFLAKE",
  "mollie": "MOLLIE",
  "magento": "MAGENTO",
};

/**
 * Registry families that deliberately have no mark.
 *
 * `profile-config` is an account entitlement (segmentation switched on for an
 * existing BigQuery source), not a connector you can add — see the note in
 * registry-mapping.ts. Listing it here is what keeps the coverage guard honest:
 * every family is either mapped or explicitly excused, so a newly added one
 * turns the guard red rather than falling through unnoticed.
 */
export const MARKLESS_REGISTRY_KEYS: ReadonlySet<string> = new Set([
  "profile-config",
]);

/**
 * Resolve a data source's `ds_key` (or a dashboard type-tag) to the connector
 * type whose logo represents it. Fail-closed: `null` when the key is unknown
 * to the registry mirror, or when its family has no mark.
 */
export function connectorTypeForDsKey(dsKey?: string | null): string | null {
  const registryKey = resolveRegistryKey(dsKey);
  if (!registryKey) return null;
  return REGISTRY_KEY_TO_CONNECTOR_TYPE[registryKey] ?? null;
}

/**
 * Resolve a catalog entry's `warehouse_kind` (the connection type, lowercased)
 * to a connector type — the fallback for direct-connect sources, which have no
 * managed ds_key. The concrete SQL engines all share the generic SQL mark
 * because that is the only one this component draws for them.
 */
export function connectorTypeForWarehouseKind(
  kind?: string | null,
): string | null {
  switch ((kind ?? "").toLowerCase()) {
    case "bigquery":
      return "CUSTOM_BIGQUERY";
    case "snowflake":
      return "SNOWFLAKE";
    case "sql":
    case "postgres":
    case "postgresql":
    case "mysql":
    case "mssql":
      return "CUSTOM_SQL";
    default:
      return null;
  }
}

const iconProps = (size: number) => ({
  width: size,
  height: size,
  style: {
    display: "block" as const,
    flexShrink: 0,
  },
});

export function ConnectorIcon({ type, size = 20 }: Props) {
  const normalized = normalizeType(type);

  switch (normalized) {
    case "FACEBOOK":
      return <MetaIcon {...iconProps(size)} />;

    case "GOOGLE_ANALYTICS":
    case "GA4_BQ_EXPORT":
      return <GoogleAnalyticsIcon {...iconProps(size)} />;

    case "GOOGLE_ADS":
      return <GoogleAdsIcon {...iconProps(size)} />;

    case "CUSTOM_BIGQUERY":
      return <BigQueryIcon {...iconProps(size)} />;

    case "SHOPIFY":
      return <ShopifyIcon {...iconProps(size)} />;

    case "MICROSOFT_ADS":
      return <MicrosoftAdsIcon {...iconProps(size)} />;

    case "EXACT":
      return <ExactIcon {...iconProps(size)} />;

    case "BREVO":
      return <BrevoIcon {...iconProps(size)} />;

    case "ACTIVECAMPAIGN":
      return <ActiveCampaignIcon {...iconProps(size)} />;

    case "KLAVIYO":
      return <KlaviyoIcon {...iconProps(size)} />;

    case "GOOGLE_SEARCH_CONSOLE":
      return <GoogleSearchConsoleIcon {...iconProps(size)} />;

    case "CUSTOM_SQL":
      return (
        <IconSql
          size={size}
          style={{ display: "block", flexShrink: 0 }}
        />
      );

    case "WOOCOMMERCE":
      return <WooCommerceIcon {...iconProps(size)} />;

    // Verity's own first-party pixel (plan 2026-10-02): no brand SVG, so a
    // Tabler glyph in the connector's registry colour, like CUSTOM_SQL.
    case "VERITY_PIXEL":
      return (
        <IconWorldCode
          size={size}
          color="var(--mantine-color-violet-6)"
          style={{ display: "block", flexShrink: 0 }}
        />
      );

    // Both spellings: the Add-source tile uses the CUSTOM_ prefix of its
    // direct-connect siblings, while an existing row carries the backend's
    // `connection_type` ("SNOWFLAKE"). No brand SVG yet — deliberately absent
    // from BRAND_CONNECTOR_TYPES, same as CUSTOM_SQL.
    case "SNOWFLAKE":
    case "CUSTOM_SNOWFLAKE":
      return (
        <IconSnowflake
          size={size}
          style={{ display: "block", flexShrink: 0 }}
        />
      );

    default: {
      const FileIcon = CONNECTOR_ICON_FILES[normalized];
      if (FileIcon) return <FileIcon {...iconProps(size)} />;
      return (
        <IconPlugConnected
          size={size}
          style={{ display: "block", flexShrink: 0 }}
        />
      );
    }
  }
}
